import React, { useMemo } from 'react';
import Image from 'next/legacy/image';
import { useTranslation } from 'next-i18next';

import { CacheReducedCaseStudy } from '@sy/ssr-types';
import Link from '~/components/SsrLink';
import { useBlogPreviewQuery } from '~/utils/hooks';
import { getTagTranslationKey } from '~/services/caseStudy/caseStudyTags';
import { Tag, TagList } from '../Tags';
import {
	Post,
	PostData,
	PostReadMore,
	PostReadMoreWrapper,
	PostThumbnail,
	PostTitle,
} from './ReducedCaseStudy.styles';

const MAX_TEXT_LENGTH = 100;

const truncate = (text?: string) => {
	if (!text || text.length < MAX_TEXT_LENGTH) {
		return text;
	}

	const words = text.substring(0, MAX_TEXT_LENGTH).split(' ');
	words.pop();
	return `${words.join(' ')}...`;
};

const ReducedCaseStudy = ({ imageSizes, onClick, caseStudy }: Props) => {
	const { t } = useTranslation('caseStudies');
	const getUrlWithPreviewQuery = useBlogPreviewQuery();

	const { coverImage, title, slug } = caseStudy;
	const tagTranslationKeys =
		caseStudy.tags?.map(tag => getTagTranslationKey(tag)).filter(Boolean) || [];

	const limitedTitle = useMemo(() => truncate(title), [title]);

	return (
		<Link
			key={slug}
			href={getUrlWithPreviewQuery(`/case-studies/${slug}`)}
			passHref
		>
			<Post onClick={onClick}>
				<PostThumbnail>
					{coverImage?.url && (
						<Image
							src={coverImage.url}
							alt={coverImage.description}
							layout="fill"
							objectFit="contain"
							sizes={imageSizes}
						/>
					)}
				</PostThumbnail>
				<PostData>
					{tagTranslationKeys.length > 0 && (
						<TagList>
							{tagTranslationKeys.map(translationKey => (
								<Tag highlight key={translationKey}>
									{t(translationKey)}
								</Tag>
							))}
						</TagList>
					)}
					<PostTitle>{limitedTitle}</PostTitle>
					<PostReadMoreWrapper>
						<PostReadMore>{t('caseStudies.overview.readMore')}</PostReadMore>
					</PostReadMoreWrapper>
				</PostData>
			</Post>
		</Link>
	);
};

type Props = {
	imageSizes: string;
	onClick: (e: any) => void | undefined;
	caseStudy: CacheReducedCaseStudy;
};

export default ReducedCaseStudy;
